// Pure grouping logic for the admin ledger (unit-tested under node in
// tests/investment-v2/activity-groups.test.mjs):
//   · date presets (this month / last 30 days / since last settlement)
//   · month keys + per-month subtotals
//   · folding identical same-day draws by one investor into a single line
//   · which "logged by" labels are worth showing (only when not the owner)

export const dayOf = (iso) => String(iso || '').slice(0, 10);
export const monthKey = (iso) => dayOf(iso).slice(0, 7);

const pad = (n) => String(n).padStart(2, '0');
export const isoDay = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

// today: a Date (UTC day is used, matching how event dates are compared).
// lastSettlement: yyyy-mm-dd or null.
export function presetRange(name, today = new Date(), lastSettlement = null) {
  const t = isoDay(today);
  if (name === 'thisMonth') return { from: t.slice(0, 7) + '-01', to: t };
  if (name === 'last30') {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 30));
    return { from: isoDay(d), to: t };
  }
  if (name === 'sinceSettlement') return lastSettlement ? { from: lastSettlement, to: '' } : null;
  return null;
}

// Which preset the current from/to matches, if any.
export function activePreset(f, today = new Date(), lastSettlement = null) {
  for (const name of ['thisMonth', 'last30', 'sinceSettlement']) {
    const r = presetRange(name, today, lastSettlement);
    if (r && r.from === (f.from || '') && r.to === (f.to || '')) return name;
  }
  return null;
}

// Latest confirmed settlement day — for one household when `who` is set
// (a sub-account resolves to its household), otherwise across everyone.
export function lastSettlementDay(settlements, who = '', householdOf = (id) => id) {
  const hh = who ? householdOf(who) : null;
  let best = null;
  (settlements || []).forEach((s) => {
    if ((s.status || 'pending') !== 'confirmed') return;
    if (hh && (s.enrolled_member_id !== hh)) return;
    const d = dayOf(s.settle_date);
    if (d && (!best || d > best)) best = d;
  });
  return best;
}

export function totalsOf(rows) {
  return rows.reduce((t, e) => ({
    count: t.count + 1,
    amount: t.amount + Math.abs(Number(e.amount || 0)),
    value: t.value + Number(e.value || 0),
    kareem: t.kareem + Number(e.kareem || 0),
    investor: t.investor + Number(e.investor || 0),
  }), { count: 0, amount: 0, value: 0, kareem: 0, investor: 0 });
}

// Rows (newest first) → [{ key: 'YYYY-MM', rows, totals }] in order.
export function groupByMonthKey(rows) {
  const out = [];
  let cur = null;
  rows.forEach((e) => {
    const k = monthKey(e.eventDate) || 'earlier';
    if (!cur || cur.key !== k) { cur = { key: k, rows: [] }; out.push(cur); }
    cur.rows.push(e);
  });
  out.forEach((g) => { g.totals = totalsOf(g.rows); });
  return out;
}

// Identical same-day draws by one investor fold into one line: same owner,
// route, day, custody, status and value-ness. Folding keeps the first row's
// position; sums are over the folded rows; the rate is kept when uniform.
export function foldKey(e) {
  return [e.member, e.srcKind || '', e.dstKind || '', dayOf(e.eventDate), e.movementStatus || '', e.noValue ? 1 : 0, e.custody || '']
    .join('|');
}

export function foldRows(rows) {
  const byKey = new Map();
  const order = [];
  rows.forEach((e) => {
    const k = foldKey(e);
    let g = byKey.get(k);
    if (!g) { g = { key: k, rows: [] }; byKey.set(k, g); order.push(g); }
    g.rows.push(e);
  });
  return order.map((g) => {
    if (g.rows.length === 1) return g.rows[0];
    const first = g.rows[0];
    const sum = (k) => g.rows.reduce((s, e) => s + Number(e[k] || 0), 0);
    const rates = new Set(g.rows.map((e) => Number(e.rate || 0)));
    const loggers = new Set(g.rows.map((e) => e.enteredByMemberId || e.enteredByRole || ''));
    return {
      ...first,
      id: 'fold-' + g.key,
      fold: g.rows,
      count: g.rows.length,
      amount: sum('amount'),
      value: sum('value'),
      kareem: sum('kareem'),
      investor: sum('investor'),
      rate: rates.size === 1 ? first.rate : null,
      mixedRate: rates.size > 1,
      mixedLogger: loggers.size > 1,
    };
  });
}

// "logged by" is only worth reading when it is NOT the row's owner.
export function loggedByIfNotOwner(label, ownerName) {
  if (!label) return '';
  return label === ownerName ? '' : label;
}
